import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import { api } from '../../api';

const ROLE_BADGE_COLORS = {
  super_admin: { bg: '#fef2f2', color: '#dc2626' },
  admin:       { bg: '#fffbeb', color: '#d97706' },
  driver:      { bg: '#f0fdf4', color: '#16a34a' },
  maintenance: { bg: '#fff7ed', color: '#ea580c' },
  dispatcher:  { bg: '#eff6ff', color: '#2563eb' },
  sales:       { bg: '#faf5ff', color: '#7c3aed' },
};

const ROLE_LABELS = {
  super_admin: 'Super Admin',
  admin: 'Admin',
  driver: 'Driver',
  maintenance: 'Maintenance',
  dispatcher: 'Dispatcher',
  sales: 'Sales',
};

function formatTime(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr.includes('T') ? dateStr : dateStr.replace(' ', 'T') + 'Z');
  return d.toLocaleTimeString('en-US', {
    timeZone: 'America/Chicago',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

function formatDate(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr.includes('T') ? dateStr : dateStr.replace(' ', 'T') + 'Z');
  const today = new Date();
  const isToday = d.toLocaleDateString('en-US', { timeZone: 'America/Chicago' }) ===
                  today.toLocaleDateString('en-US', { timeZone: 'America/Chicago' });
  if (isToday) return formatTime(dateStr);
  return d.toLocaleDateString('en-US', {
    timeZone: 'America/Chicago',
    month: 'short',
    day: 'numeric',
  }) + ' ' + formatTime(dateStr);
}

export default function WorkOrderComments({ workOrderId }) {
  const { user } = useAuth();
  const [comments, setComments] = useState([]);
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const listRef = useRef(null);

  const loadComments = useCallback(async () => {
    if (!workOrderId) return;
    try {
      const data = await api.getWorkOrderComments(workOrderId);
      setComments(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Failed to load work order comments:', err.message);
    }
  }, [workOrderId]);

  // Initial load + polling
  useEffect(() => {
    loadComments();
    const interval = setInterval(loadComments, 15000);
    return () => clearInterval(interval);
  }, [loadComments]);

  // Auto-scroll to bottom on new comments
  useEffect(() => {
    if (listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    }
  }, [comments.length]);

  async function handleSend(e) {
    e.preventDefault();
    if (!message.trim() || sending) return;

    setSending(true);
    setError('');
    try {
      const newComment = await api.addWorkOrderComment(workOrderId, message.trim());
      setComments(prev => [...prev, newComment]);
      setMessage('');
    } catch (err) {
      setError(err.message || 'Failed to send comment');
    }
    setSending(false);
  }

  function handleKeyDown(e) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend(e);
    }
  }

  return (
    <div className="wo-comments">
      <div className="wo-comments__title">Comments</div>

      <div className="wo-comments__list" ref={listRef}>
        {comments.length === 0 ? (
          <div className="wo-comments__empty">No comments yet.</div>
        ) : (
          comments.map(c => {
            const roleBadge = ROLE_BADGE_COLORS[c.role] || ROLE_BADGE_COLORS.driver;
            const isMe = c.user_id === user?.id;

            return (
              <div key={c.id} className="wo-comment">
                <div className="wo-comment__header">
                  <span className="wo-comment__name">
                    {c.full_name || 'Unknown'}{isMe ? ' (you)' : ''}
                  </span>
                  <span
                    className="wo-comment__role"
                    style={{ background: roleBadge.bg, color: roleBadge.color }}
                  >
                    {ROLE_LABELS[c.role] || c.role}
                  </span>
                  <span className="wo-comment__time">{formatDate(c.created_at)}</span>
                </div>
                <div className="wo-comment__message">{c.message}</div>
              </div>
            );
          })
        )}
      </div>

      {error && <div style={{ color: '#dc2626', fontSize: '0.8rem', padding: '4px 12px' }}>{error}</div>}

      <form className="wo-comments__input" onSubmit={handleSend}>
        <input
          type="text"
          className="form-input"
          placeholder="Type a message..."
          value={message}
          onChange={e => setMessage(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={sending}
          style={{ flex: 1 }}
        />
        <button
          type="submit"
          className="btn btn-gold btn-sm"
          disabled={!message.trim() || sending}
        >
          {sending ? 'Sending...' : 'Send'}
        </button>
      </form>

      <style>{`
        .wo-comments {
          border: 1px solid var(--border-light, #e5e7eb);
          border-radius: var(--radius-md, 8px);
          overflow: hidden;
          background: white;
        }
        .wo-comments__title {
          font-weight: 700;
          font-size: 0.95rem;
          padding: 12px 16px;
          border-bottom: 1px solid var(--border-light, #e5e7eb);
          background: var(--bg-subtle, #f9fafb);
        }
        .wo-comments__list {
          max-height: 320px;
          overflow-y: auto;
          min-height: 60px;
        }
        .wo-comments__empty {
          padding: 24px;
          text-align: center;
          color: var(--text-secondary, #6b7280);
          font-size: 0.85rem;
        }
        .wo-comment {
          padding: 12px 16px;
          border-bottom: 1px solid var(--border-light, #e5e7eb);
        }
        .wo-comment:last-child { border-bottom: none; }
        .wo-comment__header {
          display: flex;
          align-items: center;
          gap: 8px;
          margin-bottom: 4px;
          flex-wrap: wrap;
        }
        .wo-comment__name {
          font-weight: 600;
          font-size: 0.85rem;
          color: var(--text-primary, #111827);
        }
        .wo-comment__role {
          font-size: 0.7rem;
          padding: 1px 6px;
          border-radius: 999px;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.02em;
        }
        .wo-comment__time {
          font-size: 0.75rem;
          color: var(--text-secondary, #9ca3af);
          margin-left: auto;
        }
        .wo-comment__message {
          font-size: 0.9rem;
          line-height: 1.5;
          color: var(--text-primary, #374151);
          white-space: pre-wrap;
          word-break: break-word;
        }
        .wo-comments__input {
          display: flex;
          gap: 8px;
          padding: 12px;
          border-top: 1px solid var(--border-light, #e5e7eb);
          background: var(--bg-subtle, #f9fafb);
        }
      `}</style>
    </div>
  );
}
